import { useCreateContract, useGetAllStudents, useUpdateContract } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Input from '@components/V4/Inputs/Input';
import Modal from '@components/V4/Modal/Modal';
import Select from '@components/V4/Select/Select';
import { useDebounce } from '@hooks';
import { contractsApis } from '@shared/apis';
import { ContractRowDTO } from '@shared/clientTypes/DTOs/ContractsDTO';
import {
  DEFAULT_CONTRACT_CURRENCY,
  DEFAULT_CONTRACT_INTERVAL_MONTHS,
  DEFAULT_CONTRACT_PAYMENT_TERM_DAYS,
} from '@shared/constants';
import { ContractPaymentType } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { formatDate, formatMoney } from '../utils';

interface ContractFormModalProps {
  isOpen: boolean;
  contract?: ContractRowDTO | null;
  presetStudent?: { _id: string; fullName: string } | null;
  onClose: () => void;
}

type FormState = {
  studentId: string;
  studentLabel: string;
  contractNumber: string;
  contractDate: string;
  contractUrl: string;
  amount: string;
  currency: string;
  sessionsInContract: string;
  paymentType: ContractPaymentType;
  installmentsCount: string;
  intervalMonths: string;
  firstInstallmentDate: string;
  paymentTermDays: string;
  useCustomAmounts: boolean;
  installmentAmounts: string[];
};

const INPUT_DATE = 'YYYY-MM-DD';

const emptyForm = (): FormState => ({
  studentId: '',
  studentLabel: '',
  contractNumber: '',
  contractDate: moment().format(INPUT_DATE),
  contractUrl: '',
  amount: '',
  currency: DEFAULT_CONTRACT_CURRENCY,
  sessionsInContract: '',
  paymentType: ContractPaymentType.Full,
  installmentsCount: '1',
  intervalMonths: String(DEFAULT_CONTRACT_INTERVAL_MONTHS),
  firstInstallmentDate: moment().format(INPUT_DATE),
  paymentTermDays: String(DEFAULT_CONTRACT_PAYMENT_TERM_DAYS),
  useCustomAmounts: false,
  installmentAmounts: [],
});

const fromContract = (contract: ContractRowDTO): FormState => ({
  studentId: contract.student._id,
  studentLabel: contract.student.fullName,
  contractNumber: contract.contractNumber ?? '',
  contractDate: moment(contract.contractDate).format(INPUT_DATE),
  contractUrl: contract.contractUrl ?? '',
  amount: String(contract.amount),
  currency: contract.currency,
  sessionsInContract: String(contract.sessionsInContract),
  paymentType: contract.paymentType,
  installmentsCount: String(contract.installmentsCount),
  intervalMonths: String(contract.intervalMonths),
  firstInstallmentDate: moment(contract.firstInstallmentDate).format(INPUT_DATE),
  paymentTermDays: String(contract.paymentTermDays),
  useCustomAmounts: false,
  installmentAmounts: contract.installments.map((installment) => String(installment.amount)),
});

const round2 = (value: number) => Math.round(value * 100) / 100;

const ContractFormModal = (props: ContractFormModalProps) => {
  const { isOpen, contract, presetStudent, onClose } = props;
  const isEdit = !!contract;
  const toast = useToast();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [studentSearch, setStudentSearch] = useState('');
  const debouncedStudentSearch = useDebounce(studentSearch, 500);

  useEffect(() => {
    if (!isOpen) return;

    if (contract) {
      setForm(fromContract(contract));
    } else {
      setForm({
        ...emptyForm(),
        studentId: presetStudent?._id ?? '',
        studentLabel: presetStudent?.fullName ?? '',
      });
    }
    setStudentSearch('');
  }, [isOpen, contract, presetStudent]);

  const { data: students, isLoading: isStudentsLoading } = useGetAllStudents(
    { search: debouncedStudentSearch, limit: 20 },
    { enabled: isOpen && !isEdit }
  );

  const onSuccess = () => {
    toast.success(isEdit ? 'Contract updated.' : 'Contract created.');
    onClose();
  };
  const onError = (error: any) =>
    toast.error(error?.response?.data?.error?.message ?? 'Contract could not be saved.');

  const { mutate: createContract, isLoading: isCreating } = useCreateContract({
    onSuccess,
    onError,
  });
  const { mutate: updateContract, isLoading: isUpdating } = useUpdateContract(contract?._id ?? '', {
    onSuccess,
    onError,
  });

  const update = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const installmentsCount =
    form.paymentType === ContractPaymentType.Full
      ? 1
      : Math.max(1, Number(form.installmentsCount) || 1);
  const amount = Number(form.amount) || 0;
  const intervalMonths = Math.max(1, Number(form.intervalMonths) || 1);

  const previewInstallments = useMemo(() => {
    const base = round2(amount / installmentsCount);
    return Array.from({ length: installmentsCount }, (_, index) => {
      const planned =
        form.useCustomAmounts && form.installmentAmounts[index] !== undefined
          ? Number(form.installmentAmounts[index]) || 0
          : index === installmentsCount - 1
          ? round2(amount - base * (installmentsCount - 1))
          : base;
      return {
        index: index + 1,
        amount: planned,
        dueDate: moment(form.firstInstallmentDate)
          .add(index * intervalMonths, 'months')
          .toISOString(),
      };
    });
  }, [
    amount,
    installmentsCount,
    intervalMonths,
    form.firstInstallmentDate,
    form.useCustomAmounts,
    form.installmentAmounts,
  ]);

  const customAmountsSum = round2(previewInstallments.reduce((sum, item) => sum + item.amount, 0));
  const customAmountsInvalid = form.useCustomAmounts && Math.abs(customAmountsSum - amount) > 0.01;

  const isValid =
    !!form.studentId &&
    amount > 0 &&
    Number(form.sessionsInContract) >= 0 &&
    !!form.contractDate &&
    !customAmountsInvalid;

  const submitHandler = () => {
    if (!isValid) return;

    const payload: contractsApis.ContractPayload = {
      studentId: form.studentId,
      contractNumber: form.contractNumber || undefined,
      contractDate: moment(form.contractDate).toISOString(),
      contractUrl: form.contractUrl || undefined,
      amount,
      currency: form.currency || DEFAULT_CONTRACT_CURRENCY,
      sessionsInContract: Number(form.sessionsInContract) || 0,
      paymentType: form.paymentType,
      installmentsCount,
      intervalMonths,
      firstInstallmentDate: moment(form.firstInstallmentDate || form.contractDate).toISOString(),
      paymentTermDays: Number(form.paymentTermDays) || DEFAULT_CONTRACT_PAYMENT_TERM_DAYS,
      installmentAmounts: form.useCustomAmounts
        ? previewInstallments.map((item) => item.amount)
        : undefined,
    };

    if (isEdit) {
      updateContract(payload);
    } else {
      createContract(payload);
    }
  };

  const fetchedStudentOptions = (students ?? []).map((student) => ({
    label: student.fullName,
    value: student._id,
  }));
  const studentOptions = isEdit
    ? [{ label: form.studentLabel, value: form.studentId }]
    : form.studentId && !fetchedStudentOptions.some((option) => option.value === form.studentId)
    ? [{ label: form.studentLabel, value: form.studentId }, ...fetchedStudentOptions]
    : fetchedStudentOptions;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <Modal.Header title={isEdit ? 'Edit contract' : 'New contract'} />
      <Modal.Body className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
          <Select
            label="Student"
            variant="primary"
            value={form.studentId || undefined}
            options={studentOptions}
            showSearch
            filterOption={() => true}
            searchValue={studentSearch}
            onSearch={setStudentSearch}
            onSelect={(value) =>
              update({
                studentId: String(value),
                studentLabel: studentOptions.find((option) => option.value === value)?.label ?? '',
              })
            }
            notFoundContent={
              isStudentsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
            }
            isLoading={isStudentsLoading}
            isDisabled={isEdit}
            placeholder="Search student"
            isRequired
          />
          <Input
            type="text"
            name="contractNumber"
            id="contractNumber"
            label="Contract number"
            value={form.contractNumber}
            onChange={(e) => update({ contractNumber: e.target.value })}
          />
          <Input
            type="date"
            name="contractDate"
            id="contractDate"
            label="Contract date"
            value={form.contractDate}
            onChange={(e) =>
              update({
                contractDate: e.target.value,
                ...(form.firstInstallmentDate === form.contractDate
                  ? { firstInstallmentDate: e.target.value }
                  : {}),
              })
            }
            isRequired
          />
          <Input
            type="text"
            name="contractUrl"
            id="contractUrl"
            label="Contract document link"
            placeholder="https://drive.google.com/..."
            value={form.contractUrl}
            onChange={(e) => update({ contractUrl: e.target.value })}
          />
          <Input
            type="number"
            name="amount"
            id="amount"
            label={`Contract amount (${form.currency})`}
            value={form.amount}
            onChange={(e) => update({ amount: e.target.value })}
            isRequired
          />
          <Input
            type="number"
            name="sessionsInContract"
            id="sessionsInContract"
            label="Sessions in contract"
            value={form.sessionsInContract}
            onChange={(e) => update({ sessionsInContract: e.target.value })}
            isRequired
          />
          <Select
            label="Payment"
            variant="primary"
            value={form.paymentType}
            options={[
              { label: 'Paid in full', value: ContractPaymentType.Full },
              { label: 'Installments', value: ContractPaymentType.Installments },
            ]}
            onSelect={(value) =>
              update({
                paymentType: value as ContractPaymentType,
                installmentsCount:
                  value === ContractPaymentType.Full
                    ? '1'
                    : form.installmentsCount === '1'
                    ? '2'
                    : form.installmentsCount,
              })
            }
          />
          <Input
            type="number"
            name="paymentTermDays"
            id="paymentTermDays"
            label="Invoice payment term (days)"
            value={form.paymentTermDays}
            onChange={(e) => update({ paymentTermDays: e.target.value })}
          />
          {form.paymentType === ContractPaymentType.Installments ? (
            <>
              <Input
                type="number"
                name="installmentsCount"
                id="installmentsCount"
                label="Number of installments"
                value={form.installmentsCount}
                onChange={(e) => update({ installmentsCount: e.target.value })}
                isRequired
              />
              <Input
                type="number"
                name="intervalMonths"
                id="intervalMonths"
                label="Interval between installments (months)"
                value={form.intervalMonths}
                onChange={(e) => update({ intervalMonths: e.target.value })}
                isRequired
              />
            </>
          ) : null}
          <Input
            type="date"
            name="firstInstallmentDate"
            id="firstInstallmentDate"
            label={
              form.paymentType === ContractPaymentType.Full
                ? 'Payment due date'
                : 'First installment date'
            }
            value={form.firstInstallmentDate}
            onChange={(e) => update({ firstInstallmentDate: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-2 rounded-2xl bg-surfaceObject p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
              Installment plan
            </span>
            {installmentsCount > 1 ? (
              <label className="flex items-center gap-2 text-xs text-customGrey">
                <input
                  type="checkbox"
                  checked={form.useCustomAmounts}
                  onChange={(e) =>
                    update({
                      useCustomAmounts: e.target.checked,
                      installmentAmounts: previewInstallments.map((item) => String(item.amount)),
                    })
                  }
                />
                Custom amounts
              </label>
            ) : null}
          </div>
          <ul className="grid grid-cols-1 gap-2 tablet:grid-cols-2">
            {previewInstallments.map((installment, index) => (
              <li
                key={installment.index}
                className="flex items-center justify-between gap-3 rounded-xl bg-surfaceBackground px-3 py-2 text-sm"
              >
                <span className="text-customGrey">
                  #{installment.index} · {formatDate(installment.dueDate)}
                </span>
                {form.useCustomAmounts ? (
                  <input
                    type="number"
                    className="w-28 rounded-md bg-surfaceObject px-2 py-1 text-right text-white"
                    value={form.installmentAmounts[index] ?? ''}
                    onChange={(e) => {
                      const next = [...form.installmentAmounts];
                      next[index] = e.target.value;
                      update({ installmentAmounts: next });
                    }}
                  />
                ) : (
                  <span>{formatMoney(installment.amount, form.currency)}</span>
                )}
              </li>
            ))}
          </ul>
          {customAmountsInvalid ? (
            <span className="text-xs text-customRed">
              Installments sum to {formatMoney(customAmountsSum, form.currency)}, contract amount is{' '}
              {formatMoney(amount, form.currency)}.
            </span>
          ) : null}
          {!isEdit ? (
            <span className="text-xs text-customGrey">
              Active enrollments of the student are linked automatically; parent details and
              strategist come from the student profile.
            </span>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer className="mt-4 flex justify-end gap-3">
        <Button variant="secondary" buttonText="Cancel" onClick={onClose} />
        <Button
          variant="primary"
          buttonText={isEdit ? 'Save' : 'Create contract'}
          disabled={!isValid}
          isLoading={isCreating || isUpdating}
          onClick={submitHandler}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ContractFormModal;
